package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.ProjectProviderMode
import dev.lingvo.shared.ProviderCredentialSource
import dev.lingvo.shared.ProviderReadiness
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.shared.creditCost
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBotIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LIconBadge
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.components.ProviderMark
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.rememberLPresence
import dev.lingvo.site.ds.tintOn
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.projects.slug.DiscardSettingsDraftModal
import dev.lingvo.site.pages.app.settings.providerReadinessBadge
import dev.lingvo.site.pages.app.settings.providerReadinessWords
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

internal data class ProviderPresentation(
    val modeLabel: String,
    val sourceLabel: String,
    val readinessLabel: String,
    val summary: String,
    val testCostNote: String,
)

internal fun providerPresentation(settings: ProviderSettingsDto): ProviderPresentation {
    // Every label on a provider card, through the catalogue. They were literals in a `when`, which
    // the extractor cannot reach — it wraps literals at call sites and a branch result is not one —
    // so a settings page that changed language kept ten English words in the middle of it.
    val mode = when (settings.mode) {
        ProjectProviderMode.INHERIT -> tr("app.projects.slug.settings.project_provider_grid.mode_inherit", "Follow workspace policy")
        ProjectProviderMode.OVERRIDE -> tr("app.projects.slug.settings.project_provider_grid.mode_override", "Use project key")
        ProjectProviderMode.OFF -> tr("app.projects.slug.settings.project_provider_grid.mode_off", "Off")
    }
    val source = when (settings.credentialSource) {
        ProviderCredentialSource.PROJECT -> tr("app.projects.slug.settings.project_provider_grid.source_project", "Project credential")
        ProviderCredentialSource.WORKSPACE -> tr("app.projects.slug.settings.project_provider_grid.source_workspace", "Workspace credential")
        ProviderCredentialSource.PLATFORM -> tr("app.projects.slug.settings.project_provider_grid.source_platform", "Lingvo-funded credential")
        ProviderCredentialSource.NONE -> tr("app.projects.slug.settings.project_provider_grid.source_none", "No credential")
    }
    // The workspace surface's own mapper rather than a second copy of it. Two vocabularies for one
    // enum is how "Off" and "Disabled" end up on the same screen, and this file held the second one.
    val readiness = providerReadinessWords(settings.readiness)
    val testNote = when {
        settings.readiness != ProviderReadiness.READY ->
            tr("app.projects.slug.settings.project_provider_grid.test_after_setup", "Testing is available after setup is complete.")
        settings.credentialSource == ProviderCredentialSource.PLATFORM -> {
            // Money, counted through the catalogue: `if (n == 1) "credit" else "credits"` is
            // English's rule, and this sentence is the only warning before a metered call.
            trf(
                "app.projects.slug.settings.project_provider_grid.test_uses_credits",
                "Testing uses {credits}.",
                "credits" to tPlural(
                    "app.projects.slug.settings.project_provider_grid.count.credit",
                    creditCost(settings.provider),
                    "{count} Lingvo credit",
                    "{count} Lingvo credits",
                ),
            )
        }
        else -> tr("app.projects.slug.settings.project_provider_grid.test_uses_no_credits", "Testing this credential uses no Lingvo credits.")
    }
    return ProviderPresentation(
        modeLabel = mode,
        sourceLabel = source,
        readinessLabel = readiness,
        // The separator binds to the fact that follows it. As "$mode · $source" the only break
        // opportunity the card had was the space *after* the middot, so every card wrapped to
        // "Follow workspace policy ·" / "No credential" and the middot ended a line, reading as a
        // stray mark rather than as the joint between two facts.
        summary = "$mode ·\u00A0$source",
        testCostNote = testNote,
    )
}

@Composable
internal fun ProjectProviderGrid(
    providers: List<ProviderSettingsDto>,
    slug: String,
    onChanged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
    initiallyExpandedProvider: MtProvider? = null,
    updateProvider: suspend (String, MtProvider, ProjectProviderUpdateCommand) -> ProviderSettingsDto =
        { projectSlug, provider, command ->
            ApiClient.updateProvider(
                projectSlug = projectSlug,
                provider = provider,
                request = command.request,
                clearBaseUrl = command.clearBaseUrl,
                clearModel = command.clearModel,
            )
        },
) {
    val initial = providers.firstOrNull { it.provider == initiallyExpandedProvider }
    var selectedId by remember(providers, initiallyExpandedProvider) { mutableStateOf(initial?.provider?.id) }
    var retained by remember(providers, initiallyExpandedProvider) { mutableStateOf(initial) }
    val selected = providers.firstOrNull { it.provider.id == selectedId }
    // Matches the detail's own 120ms exit transition, so the card unmounts when it finishes fading
    // rather than 60ms after it has already disappeared.
    val presence = rememberLPresence(selectedId != null, Motion.FastMs)
    var detailDirty by remember(providers, initiallyExpandedProvider) { mutableStateOf(false) }
    var pendingSelection by remember(providers, initiallyExpandedProvider) {
        mutableStateOf<ProviderSelectionRequest?>(null)
    }
    // Focus belongs to the detail only when the owner asked for it. A deep link that arrives with a
    // provider already expanded must leave the caret where the reader put it.
    var focusOnEnter by remember(providers, initiallyExpandedProvider) { mutableStateOf(false) }
    // A switch cannot cross-fade two editors on top of each other: the outgoing one still owns focus
    // and an inert subtree, so the replacement waits for it to finish leaving.
    var pendingOpen by remember(providers, initiallyExpandedProvider) { mutableStateOf<String?>(null) }
    var restoreFocusTo by remember(providers, initiallyExpandedProvider) { mutableStateOf<String?>(null) }

    fun focusConfigure(providerId: String) {
        (document.getElementById(providerConfigureId(providerId)) as? HTMLElement)?.focus()
    }

    fun closeDetail() {
        // Move focus before the detail enters EXITING and becomes inert. Waiting for unmount leaves
        // keyboard and assistive-technology users focused inside an intentionally inactive subtree.
        selectedId?.let { providerId ->
            focusConfigure(providerId)
            // ...and claim it again once the detail has actually gone. Closing from a confirmation
            // means a dialog is unwinding at the same time, and whatever it does to focus on the
            // way out happens after this handler returns. Without the second pass the caret ends up
            // on `body`, which is the one place a keyboard user cannot continue from.
            restoreFocusTo = providerId
        }
        selectedId = null
    }

    fun openDetail(targetId: String) {
        retained = providers.firstOrNull { it.provider.id == targetId } ?: retained
        selectedId = targetId
        focusOnEnter = true
    }

    fun applySelection(targetId: String?) {
        when {
            targetId == null -> {
                focusOnEnter = false
                closeDetail()
            }
            // Nothing is open, so there is no exit to wait for.
            selectedId == null -> openDetail(targetId)
            // Start the outgoing exit and hand the replacement to the effect below. Focus is not
            // returned to the old trigger here; the incoming detail claims it instead.
            else -> {
                pendingOpen = targetId
                restoreFocusTo = null
                selectedId = null
            }
        }
    }

    // The shared draft guard only intercepts links and tabs, so it never sees a provider switch.
    // Closing or swapping the open detail unmounts the editor, and without this the unsaved key,
    // model, or endpoint the owner just typed would disappear with no confirmation at all.
    fun requestSelection(targetId: String?) {
        if (detailDirty) pendingSelection = ProviderSelectionRequest(targetId) else applySelection(targetId)
    }

    LaunchedEffect(presence.rendered, restoreFocusTo) {
        val providerId = restoreFocusTo ?: return@LaunchedEffect
        if (presence.rendered) return@LaunchedEffect
        restoreFocusTo = null
        focusConfigure(providerId)
    }

    LaunchedEffect(presence.rendered, pendingOpen) {
        val target = pendingOpen ?: return@LaunchedEffect
        if (presence.rendered) return@LaunchedEffect
        pendingOpen = null
        openDetail(target)
    }

    SettingsSection(
        title = t("app.projects.slug.settings.project_provider_grid.translation_providers", "Translation providers"),
        description = t("app.projects.slug.settings.project_provider_grid.choose_the_credential_source_for_this", "Choose the credential source for this project, verify readiness, and test the effective connection."),
        wide = true,
        icon = { LBotIcon() },
    ) {
        if (providers.isEmpty()) {
            LEmptyState(
                title = t("app.projects.slug.settings.project_provider_grid.no_translation_providers", "No translation providers"),
                description = t("app.projects.slug.settings.project_provider_grid.retry_when_the_provider_service_is", "Retry when the provider service is available."),
                modifier = Modifier.fillMaxWidth(),
                icon = LEmptyStateIcon.AUTOMATION,
            ) {
                LButton(t("app.projects.slug.settings.project_provider_grid.retry_providers", "Retry providers"), onChanged, variant = LButtonVariant.SECONDARY)
            }
        } else {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                Div(cardGridModifier("provider").attr("data-provider-grid", "").toAttrs()) {
                    providers.forEachIndexed { index, settings ->
                        ProviderOverviewCard(
                            settings = settings,
                            selected = selectedId == settings.provider.id,
                            index = index,
                            onConfigure = {
                                requestSelection(
                                    settings.provider.id.takeIf { it != selectedId },
                                )
                            },
                        )
                    }
                }
                val detailSettings = selected ?: retained
                if (presence.rendered && detailSettings != null) {
                    ProjectProviderDetail(
                        settings = detailSettings,
                        slug = slug,
                        presence = presence,
                        onClose = { requestSelection(null) },
                        onDirtyChanged = { detailDirty = it },
                        focusOnEnter = focusOnEnter,
                        onChanged = onChanged,
                        notify = notify,
                        updateProvider = updateProvider,
                    )
                }
            }
        }
    }

    DiscardSettingsDraftModal(
        open = pendingSelection != null,
        guardId = "project-provider-switch",
        onCancel = { pendingSelection = null },
        onDiscard = {
            val request = pendingSelection
            pendingSelection = null
            detailDirty = false
            if (request != null) applySelection(request.targetId)
        },
    )
}

/** The provider detail the owner asked for while an unsaved draft is still open; null closes it. */
private data class ProviderSelectionRequest(val targetId: String?)

@Composable
private fun ProviderOverviewCard(
    settings: ProviderSettingsDto,
    selected: Boolean,
    index: Int,
    onConfigure: () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    val presentation = providerPresentation(settings)
    LCard(
        Modifier
            .fillMaxWidth()
            .enterStaggered(index)
            .then(
                if (selected) {
                    // **A ring, not a bar down one edge.**
                    //
                    // This was `inset 3px 0 0 accent` — a coloured stripe on the left of a card,
                    // which is the most over-used selection mark there is and says nothing about
                    // what the selection did. What it actually did is expand the detail panel
                    // below, so the card is the open one in a disclosure: a ring around the whole
                    // card marks that, reads the same in every writing direction, and is the shape
                    // this system already uses for focus.
                    Modifier.border(1.px, LineStyle.Solid, palette.accent).styleModifier {
                        property(
                            "box-shadow",
                            "0 0 0 3px ${palette.accent.tintOn(palette.surface, SELECTED_RING_TINT)}",
                        )
                    }
                } else {
                    Modifier
                },
            )
            .attr("data-provider-card", settings.provider.id),
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.MD).styleModifier { property("height", "100%") }) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                ProviderMark(settings.provider)
                Column(Modifier.minWidth(0.px).gap(Space.XXS)) {
                    // `h3`, under the section's own `h2`. As an `h2` this card announced itself as a
                    // peer of "Translation providers" and every provider read as a new top-level
                    // section, which is not what the page is.
                    H3(Modifier.fontSize(TypeScale.Body).fontWeight(700).margin(0.px).toAttrs()) {
                        Text(settings.provider.displayName)
                    }
                    Span(Modifier.color(palette.textSecondary).fontSize(TypeScale.Small).toAttrs()) {
                        Text(presentation.summary)
                    }
                }
                Spacer()
                LBadge(presentation.readinessLabel, variant = providerReadinessBadge(settings.readiness))
            }
            settings.unavailableReason?.let { reason ->
                P(Modifier.color(palette.textSecondary).fontSize(TypeScale.Small).margin(0.px).toAttrs()) {
                    Text(reason)
                }
            }
            if (settings.keyUnreadable) {
                Row(Modifier.fillMaxWidth().gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
                    LWarningIcon(Modifier.iconSize(IconSize.MD).color(palette.warning))
                    Span(Modifier.color(palette.textSecondary).fontSize(TypeScale.Small).toAttrs()) {
                        Text(t("app.projects.slug.settings.project_provider_grid.stored_project_key_needs_replacement", "Stored project key needs replacement"))
                    }
                }
            }
            Spacer()
            Div(Modifier.fillMaxWidth().attr("data-provider-footer", "").toAttrs()) {
                Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                    // `Small`, not `Micro`. This is the sentence that says whether pressing the
                    // button below spends money, and `docs/DESIGN.md` gives Micro to uppercase
                    // labels with +0.08em tracking, not to sentences — it was the smallest type on
                    // the page, under a muted description set larger than it.
                    Span(Modifier.color(palette.textMuted).fontSize(TypeScale.Small).toAttrs()) {
                        Text(presentation.testCostNote)
                    }
                    LButton(
                        label = if (selected) t("app.projects.slug.settings.project_provider_grid.hide_settings", "Hide settings") else t("app.projects.slug.settings.project_provider_grid.configure", "Configure"),
                        onClick = onConfigure,
                        // Five providers, five identical "Configure" entries in a button list.
                        // The visible words come first and stay contiguous. WCAG 2.5.3 asks that
                        // an accessible name *contain* the label on the control, because a speech
                        // user says what they can see — "click Hide settings". "Hide DeepL settings"
                        // splits those two words apart and the command matches nothing.
                        accessibleLabel = if (selected) {
                            trf(
                                "app.projects.slug.settings.project_provider_grid.hide_settings_for_provider",
                                "Hide settings for {provider}",
                                "provider" to settings.provider.displayName,
                            )
                        } else {
                            trf(
                                "app.projects.slug.settings.project_provider_grid.configure_provider",
                                "Configure {provider}",
                                "provider" to settings.provider.displayName,
                            )
                        },
                        modifier = Modifier.fillMaxWidth()
                            .attr("id", providerConfigureId(settings.provider.id))
                            .attr("aria-expanded", selected.toString())
                            .attr("aria-controls", providerDetailId(settings.provider.id)),
                        // Six identical secondary buttons said nothing about where to start: a card
                        // badged "Ready" offered exactly the control a card badged "Needs setup"
                        // did. A ready provider keeps its control — it is how a key gets changed —
                        // demoted to ghost, so the cards that still need one are the cards drawn as
                        // controls. An open card keeps the secondary weight: "Hide settings" is
                        // then the pair of the detail panel below it.
                        variant = if (settings.readiness == ProviderReadiness.READY && !selected) {
                            LButtonVariant.GHOST
                        } else {
                            LButtonVariant.SECONDARY
                        },
                    )
                }
            }
        }
    }
}

/** Soft enough to read as a halo rather than a second border. */
private const val SELECTED_RING_TINT = 0.35f

internal fun providerConfigureId(providerId: String): String = "provider-configure-$providerId"
internal fun providerDetailId(providerId: String): String = "provider-detail-$providerId"
